import React, { FormEventHandler, useCallback } from 'react'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextInput from 'components/Common/Form/Input/TextInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import Group from 'components/utils/Group'
import TextArea from 'components/Common/Form/Input/TextArea'
import { formToObject } from 'lib/forms/formToObject'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import { connect } from 'react-redux'
import AgentCallbackRequestDateTimeInput from './AgentCallbackRequestDateTimeInput'

export interface RequestAgentCallbackResult {
  firstName: string;
  lastName: string;
  email: string;
  phone: { phone: string, prefix: string };
  message: string;
  callbackDate: Date;
  callbackTime: string;
  timezone: string;
}

interface MappedStateProps {
  account: App.AuthAccount;
}

function RequestAgentCallbackModal(props: MappedStateProps) {
  const { account } = props

  const modalElement = useModalElementContext<RequestAgentCallbackResult | undefined>()

  const handleCancel = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(async(e) => {
    e.preventDefault()

    const requestAgentCallbackFormData = formToObject<RequestAgentCallbackResult>(e.currentTarget)
    modalElement.resolve(requestAgentCallbackFormData)
  }, [modalElement])

  return (
    <ModalBase>
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Request a callback"/>
        <ModalBody>
          <ModalContent>
            <VerticalSpacer gap={20}>
              <BodyTextBlock variant="large">
                Share your details with us, and our support team will get back to you as soon as possible.
              </BodyTextBlock>
              <Group direction="vertical" gap={8}>
                <Group direction="horizontal" gap={8} horizontalAlign="stretch">
                  <TextInput
                    name="firstName"
                    defaultValue={account.givenName}
                    invalidErrorMessage="Invalid characters. Please use only letters and spaces"
                    label="First name *"
                    maxLength={100}
                    minLength={2}
                    pattern="^[A-Za-z ]+$"
                    placeholder="Your first name"
                    required
                    requiredErrorMessage="First name is required"
                  />
                  <TextInput
                    name="lastName"
                    defaultValue={account.surname}
                    invalidErrorMessage="Invalid characters. Please use only letters and spaces"
                    label="Last name *"
                    maxLength={100}
                    minLength={2}
                    pattern="^[A-Za-z ]+$"
                    placeholder="Your last name"
                    required
                    requiredErrorMessage="Last name is required"
                  />
                </Group>
                <PhoneInput
                  defaultPhoneNumber={account.phone}
                  defaultPhonePrefix={account.phonePrefix}
                  label="Phone number *"
                  name="phone"
                  placeholder="e.g. 412 345 678"
                  required
                  requiredErrorMessage="Phone number is required"
                />
                <AgentCallbackRequestDateTimeInput />
                <TextInput
                  invalidErrorMessage="Invalid email address"
                  defaultValue={account.email}
                  label="Email address *"
                  maxLength={100}
                  name="email"
                  placeholder="Enter your business email"
                  required
                  requiredErrorMessage="Email address is required"
                  type="email"
                />
                <TextArea
                  label="Message *"
                  maxLength={100}
                  name="message"
                  placeholder="e.g. I have a payment issue"
                  type="message"
                  requiredErrorMessage="Comment is required"
                  required
                />
              </Group>
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        <ModalFooter
        primaryActionProps={{ children: 'Submit', type: 'submit' }}
        secondaryActionProps={{ children: 'Cancel', onClick: handleCancel }}
      />
      </form>
    </ModalBase>
  )
}

export default connect<MappedStateProps, undefined, {}, App.State>((state) => {
  return {
    account: state.auth.account,
  }
})(RequestAgentCallbackModal)
